import React, { MutableRefObject, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { includes } from 'lodash-es';
import { FormSettingsEnum } from '@dev/ee-api/ee-api-models';
import { WebsitePrimaryButton } from '@dev/ee-components';
import { getFormCreatorStore } from '@/store/formCreator/actions';
import { FormCreatorStatus, FormErrorNames } from '@/store/formCreator/types';
import { StepTitle } from '../components/StepTitle';
import { StepContent } from '../components/StepContent';
import { Step1Content } from '../steps/Step1Content';
import { Step1EditContent } from '../steps/Step1EditContent';
import { Step2Content } from '../steps/Step2Content';
import { Step2EditContent } from '../steps/Step2EditContent';
import { getStepsValidity } from '../tools/getStepsValidity';
import { getStepsStatuses } from '../tools/getStepsStatuses';
import { getStep3Validity } from '../tools/getStep3Validity';
import { Step3EditContent } from '../steps/Step3EditContent';
import { Step3Content } from '../steps/Step3Content';
import { Step4EditContent } from '../steps/Step4EditContent';
import { StepPublishContent } from '../steps/StepPublishContent';
import { Step4Content } from '../steps/Step4Content';
import { Step, StepStatus } from '../types';
import { Step5EditContent } from '../steps/Step5EditContent';
import { Step5Content } from '../steps/Step5Content';

interface StepsProps {
    onExit: () => void;
    isSavedRef: MutableRefObject<boolean>;
    isPopupCreator: boolean;
}

export const useSteps = ({ onExit, isSavedRef, isPopupCreator }: StepsProps): any[] => {
    const { t } = useTranslation();
    const { stepInEdit, formObject, formPayload, status, errors, isTemplateDeselected } = useSelector(getFormCreatorStore);

    const isSaving = status === FormCreatorStatus.SAVING;

    const stepsValidity = getStepsValidity(formObject);
    const stepsStatuses = getStepsStatuses(stepsValidity, stepInEdit);

    useEffect((): void => {
        isSavedRef.current = !(formPayload || !stepsValidity[Step.Publish]);
    }, [formPayload,formObject])

    const isStep3ExternalWebsiteUrlError = !!errors?.[FormErrorNames.ACTIVATION_VIA_EMAIL_INVALID_URL] || !!errors?.[FormErrorNames.ALREADY_SUBSCRIBED_INVALID_URL] || !!errors?.[FormErrorNames.FORM_SUBMIT_INVALID_URL];
    const isStep4NotifyEmailsError = !!errors?.[FormErrorNames.NOTIFY_INVALID_EMAILS] || !!errors?.[FormErrorNames.NOTIFY_EMAILS_NEMBER_EXCEEDED] || !!errors?.[FormErrorNames.NOTIFY_EMAILS_NO_PROPER_SEPARATOR]|| !!errors?.[FormErrorNames.NOTIFY_EMAILS_NOT_ON_CONTACT_LIST];
    const isStep4SaveDisabled = isStep4NotifyEmailsError || (!!includes(formPayload?.FormSettings, FormSettingsEnum.NotifyEmails) && !formPayload?.EmailsToNotifyOnAction);

    return [
        {
            title: <StepTitle stepName={Step.Name} isEditVisible={stepsStatuses[Step.Name] === StepStatus.DoneOpen} isEditDisabled={isSaving} title={t('nameform')} />,
            content: (
                <StepContent 
                    isSaveVisible
                    isSaveDisabled={!formPayload?.Name}
                    isEditMode={stepsStatuses[Step.Name] === StepStatus.Active}
                    isNew={!stepsValidity[Step.Name]}
                    content={<Step1Content />}
                    editContent={<Step1EditContent />}
                    isPopupCreator={isPopupCreator}
                />
            ),
            status: stepsStatuses[Step.Name],
            hasErrors: stepInEdit === Step.Name || !stepsValidity[Step.Name],
        },
        {
            title: <StepTitle stepName={Step.Design} isEditVisible={stepsStatuses[Step.Design] === StepStatus.DoneOpen} isEditDisabled={isSaving} title={t('designform')} />,
            content: (
                <StepContent
                    isSaveVisible={false}
                    isSaveDisabled={!formPayload?.Html}  
                    isEditMode={stepsStatuses[Step.Design] === StepStatus.Active}
                    isNew={!stepsValidity[Step.Design]}
                    content={<Step2Content isEditMode={false}/>}
                    editContent={!isTemplateDeselected && formPayload?.FormTemplateName !== null && formObject?.FormTemplateName ? <Step2Content isEditMode /> : <Step2EditContent /> }
                    isPopupCreator={isPopupCreator}
                />
            ),
            status: stepsStatuses[Step.Design],
            hasErrors: stepInEdit === Step.Design || !stepsValidity[Step.Design],
        },
        {
            title: <StepTitle stepName={Step.Settings} isEditVisible={stepsStatuses[Step.Settings] === StepStatus.DoneOpen} isEditDisabled={isSaving} title={t('settings')} />,
            content: (
                <StepContent 
                    isSaveVisible
                    isSaveDisabled={getStep3Validity(formPayload, formObject) || isStep3ExternalWebsiteUrlError}  
                    isEditMode={stepsStatuses[Step.Settings] === StepStatus.Active}
                    isNew={!stepsValidity[Step.Settings]}
                    content={<Step3Content />} 
                    editContent={<Step3EditContent />}
                    beforeSave
                    isPopupCreator={isPopupCreator}
                />
            ),
            status: stepsStatuses[Step.Settings],
            hasErrors: stepInEdit === Step.Settings || !stepsValidity[Step.Settings],
        },
        {
            title: <StepTitle stepName={Step.Notifications} isEditVisible={stepsStatuses[Step.Notifications] === StepStatus.DoneOpen} isEditDisabled={isSaving} title={t('notifications')} />,
            content: (
                <StepContent 
                    isSaveVisible
                    isSaveDisabled={isStep4SaveDisabled}  
                    isEditMode={stepsStatuses[Step.Notifications] === StepStatus.Active}
                    isNew={!stepsValidity[Step.Notifications]}
                    content={<Step4Content />} 
                    editContent={<Step4EditContent />}
                    isPopupCreator={isPopupCreator}
                />
            ),
            status: stepsStatuses[Step.Notifications],
            hasErrors: stepInEdit === Step.Notifications || !stepsValidity[Step.Notifications],
        },
        ...(isPopupCreator ? [{
            title: <StepTitle stepName={Step.Behaviour} isEditVisible={stepsStatuses[Step.Behaviour] === StepStatus.DoneOpen} isEditDisabled={isSaving} title={t('behaviour')} />,
            content: (
                <StepContent 
                    isSaveVisible
                    isSaveDisabled={false}  
                    isEditMode={stepsStatuses[Step.Behaviour] === StepStatus.Active}
                    isNew={!stepsValidity[Step.Behaviour]}
                    content={<Step5Content />} 
                    editContent={<Step5EditContent />}  
                    isPopupCreator={isPopupCreator}
                />
            ),
            status: stepsStatuses[Step.Behaviour],
            hasErrors: stepInEdit === Step.Behaviour || !stepsValidity[Step.Behaviour],
        }] : []),
        {
            title: t('publish'),
            content: <StepPublishContent formObject={formObject} isStepActive={stepsStatuses[Step.Publish] === StepStatus.Active}/>,
            status: stepsStatuses[Step.Publish],
        },
        {
            title: '',
            content: (
                <WebsitePrimaryButton text={t('backtoforms')} onClick={onExit} />
            ),
            status: StepStatus.ContentOnly,
            hasErrors: false,
        },
    ];
};
